/** Hash-based route parsing for Quote2Cart admin */

export const ROUTE = {
  DASHBOARD: 'dashboard',
  HELP: 'help',
  QUOTES: 'quotes',
  QUOTES_CREATE: 'quotes/create',
  QUOTES_DETAIL: 'quotes/:id',
  QUOTES_EDIT: 'quotes/:id/edit',
  SETTINGS_STORE_PROFILE: 'settings/store-profile',
  SETTINGS_BASIC_SETUP: 'settings/basic-setup',
  SETTINGS_CUSTOMER_INTERFACE: 'settings/customer-interface',
  SETTINGS_EMAIL: 'settings/email',
  SETTINGS_DISTRIBUTION: 'settings/distribution',
  INSTALLATION: 'installation',
  TOOLS_DISCOUNTS: 'tools/discounts',
  TOOLS_HIDE_PRODUCTS: 'tools/hide-products',
  TOOLS_HIDE_ADD_TO_CART: 'tools/hide-add-to-cart',
  ACCOUNT_PAYMENTS: 'account/payments',
  CHAT: 'chat',
  COMPANIES: 'companies',
  CUSTOMERS: 'customers',
  CUSTOMERS_DETAIL: 'customers/:id',
  CUSTOMER_GROUPS: 'customer-groups',
};

const ROUTE_DEFINITIONS = [
  { name: 'quotes-edit', pattern: /^quotes\/([^/]+)\/edit$/, paramKeys: ['id'] },
  { name: 'quotes-create', pattern: /^quotes\/create$/ },
  { name: 'quotes-detail', pattern: /^quotes\/([^/]+)$/, paramKeys: ['id'] },
  { name: 'quotes', pattern: /^quotes$/ },
  { name: 'settings', pattern: /^settings\/(store-profile|basic-setup|customer-interface|email|distribution)$/, sectionKey: 1 },
  { name: 'installation', pattern: /^installation$/ },
  { name: 'tools-discounts', pattern: /^tools\/discounts$/ },
  { name: 'tools-hide-products', pattern: /^tools\/hide-products$/ },
  { name: 'tools-hide-add-to-cart', pattern: /^tools\/hide-add-to-cart$/ },
  { name: 'account-payments', pattern: /^account\/payments$/ },
  { name: 'chat', pattern: /^chat$/ },
  { name: 'help', pattern: /^help$/ },
  { name: 'companies', pattern: /^companies$/ },
  { name: 'customers-detail', pattern: /^customers\/([^/]+)$/, paramKeys: ['id'] },
  { name: 'customers', pattern: /^customers$/ },
  { name: 'customer-groups', pattern: /^customer-groups$/ },
  { name: 'dashboard', pattern: /^dashboard$/ },
];

function splitHash(hash) {
  const raw = (hash ?? window.location.hash ?? '')
    .replace(/^#/, '')
    .replace(/^\//, '')
    .trim();
  const qIndex = raw.indexOf('?');
  if (qIndex === -1) {
    return { path: raw || ROUTE.DASHBOARD, query: {} };
  }
  const path = raw.slice(0, qIndex) || ROUTE.DASHBOARD;
  const query = {};
  const params = new URLSearchParams(raw.slice(qIndex + 1));
  params.forEach((value, key) => {
    query[key] = value;
  });
  return { path, query };
}

function normalizeHash(hash) {
  return splitHash(hash).path;
}

/**
 * @returns {{ name: string, path: string, params: Record<string, string>, query: Record<string, string>, section?: string }}
 */
export function parseRoute(hash) {
  const { path, query } = splitHash(hash);

  for (const def of ROUTE_DEFINITIONS) {
    const match = path.match(def.pattern);
    if (!match) continue;

    const params = {};
    if (def.paramKeys) {
      def.paramKeys.forEach((key, i) => {
        params[key] = match[i + 1];
      });
    }

    if (def.name === 'quotes-detail' && params.id === 'create') {
      continue;
    }

    const result = { name: def.name, path, params, query };
    if (def.sectionKey != null) {
      result.section = match[def.sectionKey];
    }
    return result;
  }

  return { name: 'dashboard', path: ROUTE.DASHBOARD, params: {}, query: {} };
}

/** @param {string} path */
export function navigate(path) {
  const normalized = String(path || ROUTE.DASHBOARD)
    .replace(/^#/, '')
    .replace(/^\//, '')
    .trim();
  const current = (window.location.hash || '').replace(/^#/, '').replace(/^\//, '').trim();
  if (current !== normalized) {
    window.location.hash = normalized;
  }
}

/** @param {string} basePath @param {Record<string, string|undefined|null>} query */
export function withQuery(basePath, query = {}) {
  const base = String(basePath || '')
    .replace(/^#/, '')
    .replace(/^\//, '')
    .split('?')[0]
    .trim();
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value != null && value !== '') params.set(key, String(value));
  });
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

export function routePath(name, params = {}) {
  switch (name) {
    case 'quotes-detail':
      return `quotes/${params.id}`;
    case 'quotes-edit':
      return `quotes/${params.id}/edit`;
    case 'customers-detail':
      return `customers/${params.id}`;
    case 'settings':
      return `settings/${params.section || 'store-profile'}`;
    default:
      return ROUTE[name] || name;
  }
}
